import { Component, State, h, Element, Watch, Prop } from '@stencil/core';
import { FinanceOption, Model, Trim, Type } from '../../types';
import { getModels, getTypes } from '../../utils/api';
import state from '../../store';

type ModelWithTrim = Model & { first_car: Trim };

@Component({
  tag: 'kia-prislista',
  styleUrl: 'kia-prislista.scss',
  shadow: true,
})
export class Prislista {
  @State() models: ModelWithTrim[] = [];
  @State() activeModels: ModelWithTrim[] = [];
  @State() types: Type[] = [];
  @State() activeType: number = 9999;
  @Element() private el: HTMLElement;
  @State() left: boolean = true;
  @State() right: boolean = false;
  @State() vertical: boolean = this.type ? false : true;
  @Prop() type?: FinanceOption = undefined;

  @Watch('vertical')
  verticalChanged() {
    if (!this.vertical) {
      const container = this.el.shadowRoot.querySelector('.kia_price_list');
      setTimeout(() => {
        container.scrollLeft = 0;
      }, 200);
    }
  }

  hasPriceInfo = (trim: Trim) => {
    switch (state.activeFinanceOption) {
      case FinanceOption.PrivatLeasing:
        return trim.private_leasing?.length;
      case FinanceOption.Loan:
        return trim.loan?.length;
      case FinanceOption.BusinessLease:
        return trim.business_leasing?.length;
      default:
        return true;
    }
  };

  async componentWillLoad() {
    const { data, status } = await getModels();
    if (data && status === 200) {
      this.models = data;
      this.activeModels = this.models;
    }
    if (this.type) {
      state.activeFinanceOption = this.type;
      if (this.type === FinanceOption.BusinessLease) {
        const onlyBusinessModels = this.models.filter(m => m.first_car?.business_leasing.length);
        this.models = onlyBusinessModels;
        this.activeModels = onlyBusinessModels;
      }
    }
    const { data: data2, status: status2 } = await getTypes();
    if (data2 && status2 === 200) {
      this.types = data2.sort((a: Type, b: Type) => (a.name.localeCompare(b.name) ? -1 : 1));
    }
    this.el.shadowRoot.addEventListener('touchend', () => {
      setTimeout(() => {
        this.checkControls();
      }, 1000);
    });
  }

  filterTrims = (id?: number) => {
    if (id) {
      this.setActiveType(id);
    }
    let models = [...this.models];
    if (this.activeType !== 9999) {
      models = models.filter(m => m.type_id === this.activeType);
    }
    this.activeModels = models.filter(m => this.hasPriceInfo(m.first_car));
  };

  setActiveType = (id: number) => {
    this.activeType = id;

    const models = [...this.models];
    if (this.activeType === 9999) {
      this.activeModels = models;
    } else {
      this.activeModels = models.filter(m => m.type_id === id);
    }
  };

  checkControls = () => {
    setTimeout(() => {
      const container = this.el.shadowRoot.querySelector('.kia_price_list');
      if (container.scrollLeft < 1) {
        this.left = true;
      } else {
        this.left = false;
      }
      if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
        this.right = true;
      } else {
        this.right = false;
      }
    }, 500);
  };

  slideRight = () => {
    const container = this.el.shadowRoot.querySelector('.kia_price_list');
    container.scrollLeft = container.scrollLeft + container.lastElementChild.clientWidth * 1;
    this.checkControls();
  };

  slideLeft = () => {
    const container = this.el.shadowRoot.querySelector('.kia_price_list');
    container.scrollLeft = container.scrollLeft + container.lastElementChild.clientWidth * -1;
    this.checkControls();
  };

  render() {
    return (
      <div class={`kia_price_list--container ${this.vertical ? 'kia_price_list--container--vertical' : ''}`}>
        <div class="kia_price_list--top">
          <kia-filters
            filteredFinanceOption={this.type}
            types={this.types}
            activeType={this.activeType}
            filterTrimsOnFinanceOption={this.filterTrims}
            setActiveType={this.filterTrims}
          />
          <div class="kia_price_list--count">
            <p>
              <strong>{this.activeModels.length} modeller</strong>
            </p>
            <div>
              <button onClick={() => (this.vertical = true)}>
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 9H10.5V12H7.5V9Z" fill={this.vertical ? '#05141F' : '#9BA1A5'} />
                  <path d="M12 14.25H22.5V15.75H12V14.25Z" fill={this.vertical ? '#05141F' : '#9BA1A5'} />
                  <path d="M12 9.75H22.5V11.25H12V9.75Z" fill={this.vertical ? '#05141F' : '#9BA1A5'} />
                  <path d="M12 18.75H22.5V20.25H12V18.75Z" fill={this.vertical ? '#05141F' : '#9BA1A5'} />
                  <path d="M7.5 13.5H10.5V16.5H7.5V13.5Z" fill={this.vertical ? '#05141F' : '#9BA1A5'} />
                  <path d="M7.5 18H10.5V21H7.5V18Z" fill={this.vertical ? '#05141F' : '#9BA1A5'} />
                </svg>
              </button>
              <button onClick={() => (this.vertical = false)}>
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12.75 12.75H17.25V17.25H12.75V12.75ZM6.75 13.5H9.75V16.5H6.75V13.5ZM20.25 13.5H23.25V16.5H20.25V13.5Z"
                    stroke={!this.vertical ? '#05141F' : '#9BA1A5'}
                    stroke-width="1.5"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div class={'kia_price_list--wrapper'}>
          <div class={`kia_price_list ${this.vertical ? 'kia_price_list--vertical' : ''}`}>
            {this.activeModels
              .sort((a, b) => (a.weight < b.weight ? -1 : 1))
              .map((m, i) => (
                <kia-priskort index={i} filteredType={this.type} key={m.code} pricelist={true} vertical={this.vertical} code={m.code} modelWithTrim={m} />
              ))}
          </div>
          <div class={`kia_price_list--controls ${this.vertical ? 'kia_price_list--controls--vertical' : ''}`}>
            <button aria-label="Slide left" class={`${this.left ? 'hidden' : ''}`} onClick={() => this.slideLeft()}>
              <svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle opacity="0.2" cx="18.5" cy="18.5" r="18" fill="#05141F" />
                <path d="M20 23L15.5 18.5L20 14" stroke="white" stroke-width="1.5" />
              </svg>
            </button>
            <button aria-label="Slide right" class={`${this.right ? 'hidden' : ''}`} onClick={() => this.slideRight()}>
              <svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle opacity="0.2" cx="18.5" cy="18.5" r="18" fill="#05141F" />
                <path d="M17 23L21.5 18.5L17 14" stroke="white" stroke-width="1.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }
}
